import React, { useCallback, useMemo, useState, useEffect } from 'react';
import BWButton from '../BWButton';
import PropTypes from 'prop-types';
import CheckAccess from 'navigation/CheckAccess';
import _ from 'lodash';
import styled from 'styled-components';
import { InputNumber } from 'antd';
import { Empty } from 'antd';
import TrTable from './element/TrTable';
import { getDataTableColumns } from './tablePreferences';
import { PortalIcon } from 'utils/baochauTheme';

const Delelist = styled.span`
  display: ${(props) => (props?.hidden ? 'none' : '')};
  cursor: pointer;
  b {
    color: red;
  }
`;

const SelectList = styled.span`
  display: ${(props) => (props?.hidden ? 'none' : '')};
  cursor: pointer;
  b {
    color: blue;
  }
`;

const InputNumberStyled = styled(InputNumber)`
  margin-right: 6px;
  .ant-input-number-handler-wrap {
    display: none !important;
  }
`;

const Wrapper = styled.div`
  @keyframes loading {
    to {
      background-position-x: -30%;
    }
  }
  .loader {
    background-color: #ededed;
    height: 18px;
    border-radius: 7px;
    width: 100%;
  }
  td .loader {
    background-color: #ededed;
    background: linear-gradient(
        100deg,
        rgba(255, 255, 255, 0) 40%,
        rgba(255, 255, 255, 0.5) 50%,
        rgba(255, 255, 255, 0) 60%
      )
      #ededed;
    background-size: 200% 100%;
    background-position-x: 180%;
    animation: 1s loading ease-in-out infinite;
  }
`;

const DataTable = ({
  // show left
  loading,
  title,
  columns,
  data = [],
  actions,

  fieldCheck,
  noSelect,
  noPaging,
  defaultDataSelect,
  showBulkButton,
  deleteAllData,
  setCheckDelete = () => {},
  page,
  totalPages,
  totalItems,
  itemsPerPage,
  onChangeSelect = () => {},
  onChangePage,
  hiddenRowSelect,
  handleBulkAction = () => {},
  hiddenDeleteClick,
  getChildren,
  parentField,
  showTfoot,
  contentTfoot,
  dataSelectChange,
  spacing,
  onToggleSelectAllPages,
  isAllPagesSelected = false,
  selectAllPagesLabel = 'Chọn tất cả bản ghi của toàn bộ các trang',
}) => {
  const dataColumns = useMemo(() => getDataTableColumns(columns), [columns]);
  const [currentPage, setCurrentPage] = useState(parseInt(page));
  const [dataSelect, setDataSelect] = useState(defaultDataSelect ?? []);
  const [selectingAllPages, setSelectingAllPages] = useState(false);
  //data first
  useEffect(() => {
    if(!deleteAllData) {
      setCheckDelete(true);
      setDataSelect([]);
    }
  }, [deleteAllData]);

  useEffect(() => {
    if(dataSelectChange && Array.isArray(dataSelectChange) && dataSelectChange.length > 0) {
      setDataSelect(dataSelectChange ?? []);
    }
  }, [dataSelectChange]);

  const totalChecked = useMemo(() => {
    if (
      data.filter((o) => {
        if (typeof hiddenRowSelect === 'function') {
          return !hiddenRowSelect(o);
        } else {
          return true;
        }
      }).length === 0
    ) {
      return false;
    }
    const dataFilter = dataSelect.filter((o) =>
      data.some((p) => {
        if (fieldCheck) {
          return String(o[fieldCheck]) === String(p[fieldCheck]);
        } else {
          return _.isEqual(o, p);
        }
      }),
    );
    if (dataFilter.length === data.filter((o) => !hiddenRowSelect?.(o)).length) {
      return true;
    }
    return false;
  }, [data, dataSelect, fieldCheck, hiddenRowSelect]);

  const rowAction = useMemo(() => {
    return (actions ?? []).filter((p) => !p.globalAction && !Boolean(typeof p.hidden !== 'function' && p.hidden));
  }, [actions]);

  const colSpan = useMemo(() => {
    const spanSelect = noSelect ? 0 : 1;
    const spanIndex = 1;
    const spanRowAction = Boolean(rowAction.length) ? 1 : 0;
    return spanSelect + spanIndex + spanRowAction + dataColumns.length;
  }, [dataColumns, noSelect, rowAction]);

  const renderRowAction = useCallback(
    (valueRender, keyRender) => {
      return (rowAction ?? [])?.map((action) => {
        const { hidden, disabled, permission, onClick, color, icon, title, requireAssigned } = action;
        const actionClassName = `bw_btn_table bw_${color}`;
        const isHidden = Boolean(typeof hidden === 'function' ? hidden(valueRender) : hidden);

        if (isHidden) {
          return null;
        }

        const isDisabled = typeof disabled === 'function' ? disabled?.(valueRender) : disabled;
        return (
          <CheckAccess
            key={`${String(title || icon)}-${keyRender}`}
            permission={permission}
            requireAssigned={requireAssigned}>
            <button
              type='button'
              disabled={isDisabled}
              onClick={() => (isDisabled ? {} : onClick?.(valueRender, keyRender))}
              style={{
                marginRight: '2px',
                display: isHidden ? 'none' : '',
              }}
              title={title}
              aria-label={title}
              className={actionClassName}>
              <PortalIcon icon={icon} className='bw_table_action_icon' />
            </button>
          </CheckAccess>
        );
      });
    },
    [rowAction],
  );

  const totalShowRecord = useMemo(() => {
    if (data.length < itemsPerPage) {
      return data.length;
    } else if (itemsPerPage > totalItems) {
      return totalItems;
    } else {
      return itemsPerPage;
    }
  }, [data, itemsPerPage, totalItems]);

  const shouldShowPaging = useMemo(() => {
    return !noPaging && !loading && Number(totalItems) > 0 && Number(totalPages) > 0 && data?.length > 0;
  }, [noPaging, loading, totalItems, totalPages, data]);

  const renderData = useCallback(
    (valueRender, keyRender) => {
      let _dataSelect = [...dataSelect];
      const findIndex = _dataSelect.findIndex((o) => {
        if (fieldCheck) {
          return String(o[fieldCheck]) === String(valueRender[fieldCheck]);
        } else {
          return _.isEqual(o, valueRender);
        }
      });

      const flag = findIndex >= 0;
      const indexValue = ((parseInt(page) || 1) - 1) * (parseInt(itemsPerPage) || data.length || 1) + keyRender + 1;
      return (
        <TrTable
          colSpan={colSpan}
          keyRender={keyRender}
          flag={flag}
          noSelect={noSelect}
          hiddenRowSelect={hiddenRowSelect}
          indexValue={indexValue}
          _dataSelect={_dataSelect}
          setDataSelect={setDataSelect}
          columns={dataColumns}
          valueRender={valueRender}
          findIndex={findIndex}
          rowAction={rowAction}
          renderRowAction={renderRowAction}
          getChildren={getChildren}
          parentField={parentField}
          onChangeSelect={onChangeSelect}
        />
      );
    },
    [dataColumns, dataSelect],
  );

  const handleCheckAll = useCallback(async () => {
    if (typeof onToggleSelectAllPages === 'function') {
      setSelectingAllPages(true);
      try {
        const nextDataSelect = await onToggleSelectAllPages(!isAllPagesSelected, dataSelect);
        if (Array.isArray(nextDataSelect)) {
          setDataSelect(nextDataSelect);
        }
      } finally {
        setSelectingAllPages(false);
      }
      return;
    }

    const _dataSelect = [...dataSelect];
    if (totalChecked) {
      for (let i of data) {
        const findIndex = _dataSelect.findIndex((o) => {
          if (fieldCheck) {
            return String(o[fieldCheck]) === String(i[fieldCheck]);
          } else {
            return _.isEqual(o, i);
          }
        });
        if (findIndex >= 0) {
          _dataSelect.splice(findIndex, 1);
        }
        setDataSelect(_dataSelect);
      }
    } else {
      for (let i of data) {
        if (!hiddenRowSelect?.(i)) {
          const findIndex = _dataSelect.findIndex((o) => {
            if (fieldCheck) {
              return String(o[fieldCheck]) === String(i[fieldCheck]);
            } else {
              return _.isEqual(o, i);
            }
          });
          if (findIndex < 0) {
            _dataSelect.push(i);
          }
          setDataSelect(_dataSelect);
        }
      }
    }
  }, [data, dataSelect, fieldCheck, hiddenRowSelect, isAllPagesSelected, onToggleSelectAllPages, totalChecked]);

  useEffect(() => {
    onChangeSelect?.(dataSelect);
  }, [onChangeSelect, dataSelect]);

  useEffect(() => {
    setCurrentPage(totalPages ? parseInt(page) : totalPages);
  }, [page, totalPages]);

  const handeChangePage = useCallback(() => {
    if (parseInt(currentPage) !== parseInt(page)) onChangePage(currentPage);
  }, [currentPage, page]);

  const jsx_tbody =
    data?.length > 0 ? (
      <tbody>{data?.map((value, key) => renderData(value, key))}</tbody>
    ) : (
      <tbody>
        <tr>
          <td colSpan={colSpan}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Không có dữ liệu' />
          </td>
        </tr>
      </tbody>
    );

  return (
    <React.Fragment>
      <Wrapper className='bw_box_card bw_data_grid_standard'>
        <div className={spacing ? 'bw_row bw_align_items_center' : 'bw_row bw_mt_2 bw_mb_2 bw_align_items_center'}>
          <div className='bw_col_6'>
            {title}
            {Boolean(dataSelect.length) > 0 && (
              <div className='bw_show_record'>
                <p className='bw_choose_record'>
                  {showBulkButton && (
                    <button
                      className='bw_btn bw_btn_success'
                      style={{ marginRight: '8px' }}
                      type='button'
                      onClick={() => {
                        handleBulkAction(dataSelect);
                      }}>
                      <PortalIcon icon='fi fi-rr-add' className='bw_btn_icon' style={{ lineHeight: 0 }} /> Chọn
                    </button>
                  )}
                  <b>{dataSelect.length}</b> đang chọn
                  {isAllPagesSelected ? ' trên toàn bộ các trang' : ''}{' '}
                  <button id='data-table-select' type='button' className='bw_table_inline_action' onClick={() => setDataSelect([])}>
                    Bỏ chọn
                  </button>{' '}
                  <Delelist
                    id='trigger-delete'
                    hidden={hiddenDeleteClick}
                    className='bw_red bw_delete'
                    onClick={() => {
                      handleBulkAction && handleBulkAction(dataSelect, 'delete');
                    }}>
                    | <b>Xoá tất cả</b>
                  </Delelist>
                </p>
              </div>
            )}
          </div>
          <div className='bw_col_6 bw_flex bw_justify_content_right bw_btn_group'>
            {actions
              ?.filter((p) => p.globalAction && !p.hidden)
              .map((props, i) => (
                <CheckAccess key={props?.content || props?.icon || i} permission={props?.permission}>
                  <BWButton
                    style={{
                      marginLeft: '3px',
                    }}
                    iconOnly
                    {...props}
                  />
                </CheckAccess>
              ))}
          </div>
        </div>
        <div className='bw_table_responsive'>
          <table className='bw_table' style={{ width: '100%' }}>
            <thead>
              <tr>
                {!noSelect && (
                  <th className='bw_sticky bw_check_sticky'>
                    {Boolean(data.filter((o) => !hiddenRowSelect?.(o)).length) && (
                      <label className='bw_checkbox' title={selectAllPagesLabel}>
                        <input
                          type='checkbox'
                          aria-label={selectAllPagesLabel}
                          disabled={loading || selectingAllPages}
                          onChange={() => handleCheckAll()}
                          checked={typeof onToggleSelectAllPages === 'function' ? isAllPagesSelected : totalChecked}
                        />
                        <span></span>
                      </label>
                    )}
                  </th>
                )}
                <th className='bw_sticky bw_no_sticky bw_text_center'>STT</th>

                {dataColumns
                  ?.filter((value) => !value.hidden)
                  .map((p, o) => (
                    <th key={o} style={p?.styleHeader} className={p?.classNameHeader ? p?.classNameHeader : ''}>
                      {p?.header}
                    </th>
                  ))}
                {Boolean(rowAction.length) && <th className='bw_sticky bw_action_table bw_text_center'>Thao tác</th>}
              </tr>
            </thead>

            {loading ? (
              <tbody>
                {Array.from(Array(10).keys())?.map(() => {
                  return (
                    <tr className='tr'>
                      {Array.from(Array(colSpan).keys())?.map(() => {
                        return (
                          <td className='td'>
                            <div className='loader'></div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            ) : (
              jsx_tbody
            )}
            {showTfoot && contentTfoot()}
          </table>
        </div>
        {shouldShowPaging && (
          <div className='bw_row bw_mt_2 bw_show_table_page'>
            <div className='bw_col_6'>
              <p>
                Show {totalShowRecord}/{totalItems} records
              </p>
            </div>
            <div className='bw_col_6 bw_flex bw_justify_content_right bw_align_items_center'>
              <div className='bw_nav_table'>
                <button
                  type='button'
                  aria-label='Trang trước'
                  disabled={!(currentPage !== 1) || !totalPages}
                  onClick={() => {
                    onChangePage(parseInt(currentPage) - 1);
                  }}
                  className={totalPages && currentPage !== 1 ? 'bw_active' : ''}>
                  <PortalIcon icon='fi fi-rr-angle-small-left' />
                </button>
                <InputNumberStyled
                  aria-label='Trang hiện tại'
                  min={1}
                  onChange={(e) => {
                    setCurrentPage(e);
                  }}
                  onPressEnter={() => onChangePage(currentPage)}
                  onBlur={() => handeChangePage()}
                  value={currentPage}
                  max={totalPages}
                />
                <span className='bw_all_page'>/ {totalPages}</span>
                <button
                  type='button'
                  aria-label='Trang sau'
                  disabled={parseInt(totalPages) === parseInt(currentPage)}
                  onClick={() => {
                    onChangePage(parseInt(currentPage) + 1);
                  }}
                  className={!(parseInt(totalPages) === parseInt(currentPage)) ? 'bw_active' : ''}>
                  <PortalIcon icon='fi fi-rr-angle-small-right' />
                </button>
              </div>
            </div>
          </div>
        )}
      </Wrapper>
    </React.Fragment>
  );
};

DataTable.propTypes = {
  /** Title of table */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Array of table's columns */
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Array of table's data */
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Decide if table is selectable */
  selectable: PropTypes.bool,
  selectedHidden: PropTypes.bool,
  onSelectionChange: PropTypes.func,

  /** Indicate table's loading state */
  loading: PropTypes.bool,

  /** Table actions */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number,
      icon: PropTypes.node,
      content: PropTypes.string,
      onClick: PropTypes.func,
      color: PropTypes.string,
      globalAction: PropTypes.bool,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      hidden: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      permissions: PropTypes.string,
      requireAssigned: PropTypes.bool,
    }),
  ),

  /** on row click */
  onRowClick: PropTypes.func,

  /** number of pages (controlled pagination) */
  pageCount: PropTypes.number,

  /** number of rows (controlled pagination) */
  totalCounts: PropTypes.number,

  /** No Paging flag */
  noPaging: PropTypes.bool,
};

export default DataTable;
